import type { Transaction } from '@api/hooks/types/getTransactions';
import { useTransactions } from '@api/hooks/useTransactions';
import { EmptyState } from '@components/EmptyState';
import Link from '@components/link';
import { useBankProfile } from '@contexts/BankProfileContext';
import {
	type ColumnProps,
	OperationIcon,
	Table,
	Typography,
} from '@gurupay/gp-ui-library';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Capability } from 'types/roles';

import { getTransactionRenderers } from './TransactionsRenderers';

const columns: ColumnProps[] = [
	{
		label: '',
		key: 'transactionInfo',
		align: 'left',
	},
];

export const RecentTransactionsWidget = () => {
	const { selectedProfile, hasCapability } = useBankProfile();
	const navigate = useNavigate();
	const { t } = useTranslation('', { keyPrefix: 'dashboard' });

	const { data, isLoading } = useTransactions(selectedProfile?.id ?? '', 1, 5);
	const hasTransactions = isLoading || !!(data?.items ?? [])?.length;

	const handleRowClick = ({ id }: Transaction) => {
		navigate(`/payments/${id}`);
	};

	return (
		<div className="bg-neutral-white p-space-24 rounded-lg flex flex-col items-center h-full">
			<div className="flex justify-between w-full items-center">
				<Typography className="text-neutral-800 font-medium" level="small-text">
					{t('recentTransactions.title')}
				</Typography>

				{hasTransactions && (
					<Link
						className="ml-auto h-5 text-brand-secondary-600 hover:text-neutral-950 hover:border-b transition"
						to="/operations"
					>
						{t('recentTransactions.allTransactions')}
					</Link>
				)}
			</div>

			<Table
				className="p-space-8 [&_td]:p-space-8 [&_tr:hover_.approval-icon]:bg-neutral-100"
				columns={columns}
				emptyTablePlaceholder={
					<EmptyState
						buttonProps={
							hasCapability(Capability.CREATE_PAYMENT)
								? {
										children: t('recentTransactions.createPayment'),
										href: '/payments/sepa',
									}
								: undefined
						}
						icon={<OperationIcon />}
						title={t('recentTransactions.noTransactions')}
					/>
				}
				isLoading={isLoading}
				onRowClick={handleRowClick}
				renders={getTransactionRenderers(t)}
				rows={data?.items ?? []}
			/>
		</div>
	);
};
